// Spanish copy of the landing, keyed by the English text on the page. Anything missing falls back to English.
// Client quotes in the reviews stay as the clients wrote them.
export type Lang = "en" | "es";

export const LANG_KEY = "mtLanguage";

/** ?lang= wins (and is remembered), then the visitor's last choice; otherwise English, always. */
export function initialLang(): Lang {
  try {
    const param = new URLSearchParams(window.location.search).get("lang")?.toLowerCase();
    if (param === "es" || param === "en") {
      localStorage.setItem(LANG_KEY, param);
      return param;
    }
    const saved = localStorage.getItem(LANG_KEY);
    if (saved === "es" || saved === "en") return saved;
  } catch {
    // Private mode: fall through.
  }
  return "en";
}

export const ES: Record<string, string> = {
  // Page
  "Mark&Ting Agency | Content, Ads & Automation": "Mark&Ting Agency | Contenido, anuncios y automatización",
  "Growth systems for businesses ready to scale": "Sistemas de crecimiento para negocios listos para escalar",

  // Header and menu
  "Home": "Inicio",
  "Services": "Servicios",
  "Why us": "Por qué nosotros",
  "Work": "Trabajos",
  "Reviews": "Reseñas",
  "Team": "Equipo",
  "FAQs": "Preguntas",
  "Onboarding": "Cómo empezamos",
  "Contact": "Contacto",
  "Book a call": "Agenda una llamada",
  "Menu": "Menú",
  "Close menu": "Cerrar menú",
  "/ Explore": "/ Explorar",
  "Language": "Idioma",

  // Hero
  "Content + Ads + Automation": "Contenido + Anuncios + Automatización",
  "Build attention.": "Atrae atención.",
  "Turn it into revenue.": "Conviértela en ventas.",
  "We help businesses grow with content, paid ads, automation, and AI systems that capture leads, follow up faster, and turn attention into revenue.":
    "Ayudamos a los negocios a crecer con contenido, anuncios pagados, automatización y sistemas de IA que capturan prospectos, dan seguimiento más rápido y convierten la atención en ventas.",
  "Book Strategy Call": "Agenda tu llamada",
  "See Our Work": "Ver nuestro trabajo",
  "See our Instagram": "Ver nuestro Instagram",
  "Built for businesses that want more than marketing": "Para negocios que quieren más que marketing",
  "They want systems that grow with them.": "Quieren sistemas que crezcan con ellos.",
  "Trusted by businesses like yours": "La confianza de negocios como el tuyo",

  // Services
  "What we can ": "Lo que podemos ",
  "do for you.": "hacer por ti.",
  "Content, paid ads, websites, automation, and AI infrastructure built to attract qualified attention and turn interest into revenue.":
    "Contenido, anuncios pagados, sitios web, automatización e infraestructura de IA para atraer atención calificada y convertir el interés en ventas.",
  "We do not just post content. We build a connected growth engine.": "No solo publicamos contenido. Construimos un motor de crecimiento conectado.",
  "Video Production": "Producción de video",
  "Paid Media": "Publicidad pagada",
  "AI & Systems": "IA y sistemas",
  "Web & CRO": "Web y conversión",
  "Real Estate": "Bienes raíces",
  "Short-Form Video & Content Production": "Video corto y producción de contenido",
  "High-Converting Paid Advertising": "Publicidad pagada que convierte",
  "Growth Systems & AI Implementation": "Sistemas de crecimiento e implementación de IA",
  "Websites & Conversion Landing Pages": "Sitios web y landing pages que convierten",
  "Real Estate Media": "Contenido para bienes raíces",
  "High-retention vertical videos, professional drone footage, and visual storytelling built for Instagram, TikTok, and YouTube Shorts.":
    "Videos verticales que retienen, tomas profesionales con dron y narrativa visual para Instagram, TikTok y YouTube Shorts.",
  "Targeted campaigns across Meta and Google that place your offer in front of ready-to-buy local and regional customers.":
    "Campañas segmentadas en Meta y Google que ponen tu oferta frente a clientes locales y regionales listos para comprar.",
  "Custom lead capture workflows, CRM pipelines, and automated assistants that help your team respond while prospects are still interested.":
    "Flujos de captura de prospectos a la medida, pipelines de CRM y asistentes automáticos que ayudan a tu equipo a responder mientras el prospecto sigue interesado.",
  "Custom web experiences designed to tell your story, strengthen organic visibility, and turn visits into qualified enquiries.":
    "Experiencias web a la medida que cuentan tu historia, fortalecen tu visibilidad orgánica y convierten visitas en solicitudes calificadas.",
  "Listing walkthroughs, aerial property media, and personal brand content for realtors, builders, and properties that need to stand out.":
    "Recorridos de propiedades, tomas aéreas y contenido de marca personal para agentes, constructores y propiedades que necesitan destacar.",
  "Monthly filming with professional camera gear": "Grabaciones mensuales con equipo profesional",
  "High-tempo editing, typography and sound design": "Edición dinámica, tipografía y diseño de sonido",
  "4K drone footage for exteriors and commercial spaces": "Tomas con dron en 4K de exteriores y espacios comerciales",
  "Content calendars planned around buyer intent": "Calendarios de contenido pensados en la intención de compra",
  "Meta Ads across feeds, stories and reels": "Meta Ads en feed, historias y reels",
  "Google Search and local campaigns": "Campañas de búsqueda y locales en Google",
  "Direct copywriting and ongoing creative testing": "Copy directo y pruebas creativas constantes",
  "Conversion tracking and clear performance reporting": "Medición de conversiones y reportes claros de resultados",
  "Automated WhatsApp and Instagram DM responses": "Respuestas automáticas en WhatsApp y mensajes de Instagram",
  "CRM pipelines with automated follow-ups": "Pipelines de CRM con seguimiento automático",
  "AI assistants that answer and qualify leads day or night": "Asistentes de IA que responden y califican prospectos de día o de noche",
  "Lead routing directly to your sales team": "Prospectos enviados directo a tu equipo de ventas",
  "Fast, mobile-first builds": "Sitios rápidos, pensados primero para celular",
  "Clear conversion and booking pathways": "Rutas claras para convertir y agendar",
  "A clean technical SEO foundation": "Una base técnica de SEO limpia",
  "Contact and quote capture connected to your workflow": "Formularios de contacto y cotización conectados a tu proceso",
  "4K property walkthrough videos": "Videos de recorrido en 4K",
  "HDR interior and exterior photography": "Fotografía HDR de interiores y exteriores",
  "Drone footage that shows context and scale": "Tomas con dron que muestran entorno y escala",
  "Agent content that builds local authority": "Contenido del agente que construye autoridad local",
  "Start now": "Empezar ahora",
  "See work": "Ver trabajos",

  // Chats and work scenes
  "Online": "En línea",
  "typing…": "escribiendo…",
  "Message": "Mensaje",
  "Mark&Ting Assistant": "Asistente Mark&Ting",
  "Hi! I would like to grow my business.": "¡Hola! Quiero hacer crecer mi negocio.",
  "Great — what would you like help with first?": "¡Excelente! ¿Con qué te gustaría empezar?",
  "More qualified leads.": "Más prospectos calificados.",
  "Perfect. I can book a strategy call for tomorrow at 11:00 AM.": "Perfecto. Te agendo una llamada de estrategia mañana a las 11:00 AM.",
  "How is the new campaign doing?": "¿Cómo va la nueva campaña?",
  "It is bringing in qualified responses, and the strongest creative is clear.": "Está trayendo respuestas calificadas y ya se ve cuál es el mejor creativo.",
  "What should we do next?": "¿Qué hacemos ahora?",
  "I recommend testing the next variation around that winning angle.": "Te recomiendo probar una nueva variación con ese ángulo ganador.",
  "Hi! I would like a quote from the website.": "¡Hola! Quiero una cotización desde el sitio web.",
  "Happy to help. Would tomorrow afternoon work for a quick consultation?": "Con gusto. ¿Te funciona mañana en la tarde para una consulta rápida?",
  "Yes, that works for me.": "Sí, me funciona.",
  "Perfect — your consultation is booked. We will send the details here.": "Perfecto, tu consulta quedó agendada. Te enviamos los detalles por aquí.",
  "I need video and photos for a new listing.": "Necesito video y fotos para una nueva propiedad.",
  "Absolutely. When will the property be ready?": "Claro. ¿Cuándo estará lista la propiedad?",
  "Thursday morning works best.": "El jueves en la mañana es mejor.",
  "Your media session is confirmed for Thursday at 10:00 AM.": "Tu sesión quedó confirmada para el jueves a las 10:00 AM.",
  "Campaign": "Campaña",
  "Leads ↑": "Prospectos ↑",
  "Cost per lead ↓": "Costo por prospecto ↓",
  "Best creative ✓": "Mejor creativo ✓",
  "4K · Drone": "4K · Dron",
  "yourbusiness.com": "tunegocio.com",
  "Site live ✓": "Sitio publicado ✓",
  "Campaign optimized ✓": "Campaña optimizada ✓",
  "Listing delivered ✓": "Propiedad entregada ✓",
  "Building your site…": "Construyendo tu sitio…",
  "Optimizing your campaign…": "Optimizando tu campaña…",
  "Shooting and editing…": "Grabando y editando…",

  "Mark&Ting Content": "Contenido Mark&Ting",
  "We need fresh content for Instagram and TikTok.": "Necesitamos contenido nuevo para Instagram y TikTok.",
  "Great! We can film this week and deliver edited reels.": "¡Excelente! Podemos grabar esta semana y entregarte los reels editados.",
  "Friday works for us.": "El viernes nos funciona.",
  "Perfect — your filming day is booked for Friday at 9:00 AM.": "Perfecto, tu día de grabación quedó agendado para el viernes a las 9:00 AM.",
  "Editing": "Editando",
  "Your reel": "Tu reel",
  "Captions": "Subtítulos",
  "Hook": "Gancho",
  "Reel published ✓": "Reel publicado ✓",
  "Editing your reels…": "Editando tus reels…",

  // Video wall
  "Scroll-stopping media": "Contenido que detiene el scroll",
  "Content that stops the scroll ": "Contenido que detiene el scroll ",
  "and drives real revenue.": "y genera ventas reales.",
  "A snapshot of the high-retention content we produce for real businesses every week.": "Una muestra del contenido que retiene y que producimos cada semana para negocios reales.",
  "See the full portfolio": "Ver todo el portafolio",
  "Login": "Acceso",
  "More on Instagram": "Más en Instagram",

  // Comparison
  "The clear difference": "La diferencia es clara",
  "Why businesses choose ": "Por qué los negocios eligen ",
  "See how one connected team compares when content, ads, websites, and follow-up need to work together.":
    "Mira cómo se compara un solo equipo conectado cuando el contenido, los anuncios, el sitio web y el seguimiento tienen que trabajar juntos.",
  "What you get": "Lo que obtienes",
  "In-house team": "Equipo interno",
  "In-house": "Equipo interno",
  "Freelancers": "Freelancers",
  "Typical agencies": "Agencias tradicionales",
  "Agencies": "Agencias",
  "Choose a comparison": "Elige una comparación",
  "Included": "Incluido",
  "Not included": "No incluido",
  "Content, ads, web and automation under one strategy": "Contenido, anuncios, web y automatización bajo una sola estrategia",
  "Our own crew for video, photo and drone": "Nuestro propio equipo de video, foto y dron",
  "Automated lead follow-up (WhatsApp, DMs, CRM)": "Seguimiento automático de prospectos (WhatsApp, mensajes, CRM)",
  "Campaigns in English and Spanish": "Campañas en inglés y español",
  "No long-term contracts": "Sin contratos a largo plazo",
  "Direct line to the people doing the work": "Trato directo con quien hace el trabajo",
  "No hiring, training or managing staff": "Sin contratar, capacitar ni administrar personal",
  "Focused on leads and sales, not just likes": "Enfocados en prospectos y ventas, no solo en likes",

  // Industries
  "Tailored solutions": "Soluciones a la medida",
  "Deep expertise across ": "Experiencia profunda en ",
  "high-impact industries.": "industrias de alto impacto.",
  "We understand the specific client journeys and sales cycles of your industry.": "Entendemos el recorrido de los clientes y los ciclos de venta de tu industria.",
  "#Restaurants & Hospitality": "#Restaurantes y hospitalidad",
  "#RealEstate & Lending": "#BienesRaíces y créditos",
  "#Automotive & Repair": "#Automotriz y talleres",
  "#Healthcare & Clinics": "#Salud y clínicas",
  "#Home & Industrial Services": "#Hogar y servicios industriales",
  "#Faith & Community": "#Fe y comunidad",
  "#B2B Professional Services": "#Servicios profesionales B2B",
  "See how we grow your industry": "Mira cómo hacemos crecer tu industria",

  // Reviews
  "What Clients": "Lo que más",
  "Value Most.": "valoran los clientes.",
  "Built for businesses that want more than marketing — they want systems that help them grow with consistency.":
    "Para negocios que quieren más que marketing: quieren sistemas que los ayuden a crecer con constancia.",
  "Rating": "Calificación",

  // Team
  "The people behind the results": "Las personas detrás de los resultados",
  "The team behind ": "El equipo detrás de ",
  "your growth.": "tu crecimiento.",
  "Strategy, content, design and technology — built by the people you work with directly.": "Estrategia, contenido, diseño y tecnología, hechos por las personas con las que trabajas directamente.",
  "Founder": "Fundador",
  "Co-Founder": "Cofundador",
  "Director of Operations": "Directora de Operaciones",
  "Community Manager": "Community Manager",
  "Video Editor": "Editor de video",
  "Programmer": "Programador",
  "An innovative leader with a background in Financial Direction from Universidad Anáhuac CDMX and experience at Nu Bank. After studying in Rome and earning recognition at the Unilever Future Leaders League in London, he now builds systems that drive growth and empower the business community.":
    "Un líder innovador con formación en Dirección Financiera por la Universidad Anáhuac CDMX y experiencia en Nu Bank. Tras estudiar en Roma y ser reconocido en el Unilever Future Leaders League de Londres, hoy construye sistemas que impulsan el crecimiento y fortalecen a la comunidad empresarial.",
  "A growth-driven leader with a background in International Business from UIW and an MBA from MCI. With experience in Germany, Austria and finance at Grupo Salinas, he focuses on systems that help businesses scale sustainably.":
    "Un líder enfocado en el crecimiento, con formación en Negocios Internacionales por UIW y un MBA por MCI. Con experiencia en Alemania, Austria y en finanzas en Grupo Salinas, se enfoca en sistemas que ayudan a los negocios a escalar de forma sostenible.",
  "Keeps every project moving, from planning and timelines to delivery, so each client always knows what comes next.":
    "Mantiene cada proyecto en movimiento, desde la planeación y los tiempos hasta la entrega, para que cada cliente siempre sepa qué sigue.",
  "A digital marketing enthusiast who believes social media is for connecting, not just posting. Her approach puts strategy over gimmicks and turns online engagement into loyal customers.":
    "Una apasionada del marketing digital que cree que las redes sociales son para conectar, no solo para publicar. Su enfoque pone la estrategia por encima de los trucos y convierte la interacción en clientes leales.",
  "The creative eye behind our clients' scroll-stopping videos. He edits dynamic, high-retention content designed to make an immediate impact and support measurable business results.":
    "El ojo creativo detrás de los videos que detienen el scroll de nuestros clientes. Edita contenido dinámico y de alta retención, pensado para causar impacto inmediato y apoyar resultados medibles.",
  "The wizard behind the code. He builds distinctive websites engineered for speed, conversion and strong organic visibility.":
    "El mago detrás del código. Construye sitios web únicos, pensados para la velocidad, la conversión y una fuerte visibilidad orgánica.",

  // FAQs
  "Frequently Asked Questions.": "Preguntas frecuentes.",
  "Everything you need to know about working with Mark & Ting and how we help businesses grow.": "Todo lo que necesitas saber para trabajar con Mark & Ting y cómo ayudamos a los negocios a crecer.",
  "How does working with Mark & Ting start?": "¿Cómo empieza el trabajo con Mark & Ting?",
  "We start with a discovery conversation to understand your business, goals, and where you may be losing visibility, leads, or opportunities. From there, we recommend the right growth system.":
    "Empezamos con una conversación para entender tu negocio, tus metas y dónde podrías estar perdiendo visibilidad, prospectos u oportunidades. A partir de ahí te recomendamos el sistema de crecimiento adecuado.",
  "What types of businesses do you work with?": "¿Con qué tipo de negocios trabajan?",
  "We work primarily with service-based businesses and realtors looking to generate more clients through content, ads, and automation systems.":
    "Trabajamos principalmente con negocios de servicios y agentes inmobiliarios que quieren conseguir más clientes con contenido, anuncios y sistemas de automatización.",
  "Do I need to commit to a long-term contract?": "¿Tengo que firmar un contrato a largo plazo?",
  "No. We focus on real partnerships, but we don’t believe in locking businesses into something that doesn’t create value.":
    "No. Buscamos relaciones reales, pero no creemos en amarrar a un negocio a algo que no le genera valor.",
  "What services can be included in my package?": "¿Qué servicios puede incluir mi plan?",
  "Depending on your needs, your package can include content creation, social media management, Meta Ads, Google Ads, landing pages, branding, websites, automation, CRM setup, and lead follow-up systems.":
    "Según lo que necesites, puede incluir creación de contenido, manejo de redes sociales, Meta Ads, Google Ads, landing pages, branding, sitios web, automatización, configuración de CRM y sistemas de seguimiento de prospectos.",
  "Do you only help with social media?": "¿Solo ayudan con redes sociales?",
  "No. We help businesses grow with content, ads, and automation that work together as one system.":
    "No. Ayudamos a los negocios a crecer con contenido, anuncios y automatización que trabajan juntos como un solo sistema.",
  "How do I know which package is right for my business?": "¿Cómo sé qué plan es el adecuado para mi negocio?",
  "That’s exactly what the strategy call is for. We’ll help identify what makes sense based on your stage and goals.":
    "Para eso es la llamada de estrategia. Te ayudamos a identificar lo que tiene sentido según tu etapa y tus metas.",

  // Onboarding
  "Okay… But What Happens": "Bien… ¿y qué pasa",
  "After I Say Yes?": "cuando digo que sí?",
  "Discovery & Alignment": "Descubrimiento y alineación",
  "We begin with a strategic conversation to understand your business, identify opportunities, and determine if we’re the right fit.":
    "Empezamos con una conversación estratégica para entender tu negocio, encontrar oportunidades y ver si somos el equipo indicado.",
  "Onboarding & Access": "Arranque y accesos",
  "We collect everything needed to build the right system — goals, offer, platforms, branding, and essential access.":
    "Reunimos todo lo necesario para construir el sistema correcto: metas, oferta, plataformas, branding y accesos esenciales.",
  "Strategy & System Setup": "Estrategia y configuración del sistema",
  "We define the strategy and build the technical infrastructure needed to attract, capture, and convert leads.":
    "Definimos la estrategia y construimos la infraestructura técnica para atraer, capturar y convertir prospectos.",
  "Launch & Execution": "Lanzamiento y ejecución",
  "We walk you through the setup, align on execution, launch, and move into ongoing optimization and growth.":
    "Te explicamos la configuración, alineamos la ejecución, lanzamos y pasamos a la optimización y el crecimiento continuos.",

  // Contact window
  "Book a strategy call": "Agenda una llamada de estrategia",
  "Stop losing revenue to ": "Deja de perder ventas por ",
  "inconsistent marketing.": "un marketing inconsistente.",
  "Let’s talk about the right system for you.": "Hablemos del sistema adecuado para ti.",
  "Email us": "Escríbenos",
  "Call or text": "Llámanos o mándanos mensaje",
  "More work": "Más trabajos",
  "@markandtingagency on Instagram ↗︎": "@markandtingagency en Instagram ↗︎",
  "Name": "Nombre",
  "Your name": "Tu nombre",
  "E-mail*": "Correo*",
  "Your email": "Tu correo",
  "Website": "Sitio web",
  "Your website": "Tu sitio web",
  "I'm interested in": "Me interesa",
  "Content & Ads": "Contenido y anuncios",
  "Automation & AI": "Automatización e IA",
  "Real estate media": "Contenido para bienes raíces",
  "Not sure yet": "Aún no lo sé",
  "Your message": "Tu mensaje",
  "Sending...": "Enviando...",
  "Schedule Call": "Agendar llamada",
  "Thanks! We'll be in touch soon.": "¡Gracias! Te contactaremos pronto.",
  "We could not send automatically. Opening your email app...": "No se pudo enviar automáticamente. Abriendo tu app de correo...",
  "Close": "Cerrar",

  // Footer
  "Let’s Grow Your Business Smarter.": "Hagamos crecer tu negocio de forma más inteligente.",
  "Content, ads, and automation built to help Hispanic businesses and realtors grow with more consistency, visibility, and results.":
    "Contenido, anuncios y automatización para ayudar a negocios hispanos y agentes inmobiliarios a crecer con más constancia, visibilidad y resultados.",
  "Your Email": "Tu correo",
  "/Explore": "/Explorar",
  "/Resources": "/Recursos",
  "All Projects": "Todos los proyectos",
  "Privacy Policy": "Aviso de privacidad",
  "Terms of Service": "Términos del servicio",
  "/Socials": "/Redes",
  "© 2026 Mark&Ting. All rights reserved.": "© 2026 Mark&Ting. Todos los derechos reservados.",
};
